import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { monthsLowerCase } from "../../consts/forecast";
import { useState, useEffect, useRef, useMemo } from "react";
import { OTBProductSelect } from "../otb/OTBProductSelect";
import { DropdownByProductSettings } from "./DropdownByProductSettings";
import { monthKeysForecastEnum } from "../../consts/quickbooksReports";
import { formatPrice, getVarianceColor } from "../../helpers/common";
import { ACTUAL, FORECAST, UNITS_ACTIVE, UNITS_FORECAST, UNITS_SOLD_ACTUAL, VARIANCE } from "../../consts/forecastPlanning/client";

const monthsLong = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

export const ActualsVSForecastTable = ({
    selectedForecastYear,
    forecastProducts,
    activeActuals,
    summaryActuals,
    plannedActuals,
    monthlyAllocationData,
    setForecastProducts,
    annualGoal,
    groupsBySelect
}) => {

    const hiddenRowClassname = "invisible";
    const [addProductToForecastOpened, setAddProductToForecastOpened] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [openedActionsByRow, setOpenedActionsByRow] = useState({
        id: "",
        opened: false
    });
    const actionsBtnRefs = useRef({});
    const [pctRevInputById, setPctRevInputById] = useState({});
    const [aovInputById, setAovInputById] = useState({});
    const scrollContainerRef = useRef(null);
    const [searchValue, setSearchValue] = useState("");

    const revPctTotalRaw = useMemo(() => {
        return (forecastProducts || []).reduce((acc, product) => {
            const raw = pctRevInputById[product.id];
            const val = raw != null && raw !== "" ? parseFloat(raw) : Number(product.pctRev || 0);
            return acc + (isNaN(val) ? 0 : val);
        }, 0);
    }, [forecastProducts, pctRevInputById]);

    const revPctTotalRounded = useMemo(() => {
        const n = Number(revPctTotalRaw) || 0;
        return Math.round(n * 10) / 10;
    }, [revPctTotalRaw]);

    const revPctBadgeClass = useMemo(() => {
        if (revPctTotalRounded > 100) return "bg-red-100 text-red-800";
        if (revPctTotalRounded < 100) return "bg-yellow-100 text-yellow-800";
        return "bg-green-100 text-green-800";
    }, [revPctTotalRounded]);

    const availableProductOptions = useMemo(() => {
        const usedIds = new Set((forecastProducts || []).map((p) => p.id));
        return (groupsBySelect || []).filter((opt) => !usedIds.has(opt.value));
    }, [groupsBySelect, forecastProducts]);

    // View-only filtering of rendered products (does not affect calculations)
    const renderedForecastProducts = useMemo(() => {
        const query = String(searchValue || "").trim().toLowerCase();
        if (!query) return forecastProducts || [];
        return (forecastProducts || []).filter((p) => {
            const label = (groupsBySelect || []).find((g) => g.value === p.id)?.label || "";
            const sku = String(p?.sku || p?.product_sku || "");
            const idStr = String(p?.id || "");
            return (
                label.toLowerCase().includes(query) ||
                sku.toLowerCase().includes(query) ||
                idStr.toLowerCase().includes(query)
            );
        });
    }, [forecastProducts, groupsBySelect, searchValue]);

    const getDerivedData = (forecastProduct) => {
        // Here need to confirm logic, because on the v0 we have monthly allocation as (8.53 = 0.0853)
        const pct = Number(forecastProduct.pctRev || 0) / 1000;
        // Normalize allocation into a 12-length array ordered by monthsLowerCase
        const monthlyAllocation = (monthsLowerCase || []).map((key) => Number((monthlyAllocationData || {})[key] || 0) / 10);
        const currentAnnualGoal = Number(annualGoal || 0);
        const toMonthNumber = (keyOrNumber) => {
            if (typeof keyOrNumber === "number") return keyOrNumber;
            const idx = (monthsLowerCase || []).indexOf(String(keyOrNumber || "").toLowerCase());
            return idx >= 0 ? idx + 1 : null;
        };
        const startMonthNum = toMonthNumber(forecastProduct.start_month) || 1;
        const endMonthNum = toMonthNumber(forecastProduct.end_month) || 12;
        const isActive = (monthNumber) => monthNumber >= startMonthNum && monthNumber <= endMonthNum;

        // const getRampMultiplier = (month) => {
        //     if (forecastProduct.ramp === "months" && isActive(month)) {
        //         const monthsFromStart = month - startMonthNum;
        //         if (monthsFromStart === 0) return 0.4;
        //         if (monthsFromStart === 1) return 0.7;
        //         return 1.0;
        //     };
        //     return 1.0;
        // };
        
        const monthlyForecast = monthlyAllocation.map((alloc, index) => {
                const month = index + 1;
                if (!isActive(month)) return 0;
            return currentAnnualGoal * pct * alloc;
        });
        const annualForecast = monthlyForecast.reduce((sum, val) => sum + val, 0);

        const monthlyForecastByMonth = (monthsLowerCase || []).reduce((acc, key, i) => {
            acc[key] = Number(monthlyForecast?.[i] || 0);
            return acc;
        }, {});
        const monthlyUnits = monthlyForecast.map((forecast) => (Number(forecastProduct.aov || 0) > 0 ? Math.round(forecast / Number(forecastProduct.aov || 0)) : null))
        const monthlyUnitsByMonth = (monthsLowerCase || []).reduce((acc, key, i) => {
            acc[key] = monthlyUnits?.[i] ?? null;
            return acc;
        }, {});
        const annualUnits = monthlyUnits.reduce((acc, val) => {
            return acc + val
        }, 0);

        // Pull actuals and units sold per-group from plannedActuals API response
        const to2 = (n) => Number(Number(n || 0).toFixed(2));
        const groupActual = (plannedActuals || []).find((a) => String(a.group_id) === String(forecastProduct.id));
        const monthlyRevenueByMonth = (monthsLowerCase || []).reduce((acc, monthKey) => {
            const val = groupActual?.monthly_actuals_by_month?.[monthKey];
            acc[monthKey] = to2(val ?? 0);
            return acc;
        }, {});
        const annualActual = to2(
            groupActual?.annual_actual ?? Object.values(monthlyRevenueByMonth || {}).reduce((sum, v) => sum + Number(v || 0), 0)
        );
        const monthlyDifferenceByMonth = (monthsLowerCase || []).reduce((acc, monthKey) => {
            const revenue = Number(monthlyRevenueByMonth?.[monthKey] ?? 0);
            const forecast = Number(monthlyForecastByMonth?.[monthKey] ?? 0);
            acc[monthKey] = to2(revenue - forecast);
            return acc;
        }, {});
        const totalDifference = Object.values(monthlyDifferenceByMonth || {}).reduce(
            (sum, value) => sum + Number(value || 0),
            0
        );

        // Units sold actuals from plannedActuals
        const soldUnitsByMonth = (monthsLowerCase || []).map((monthKey) => {
            return Number(groupActual?.monthly_units_sold_by_month?.[monthKey] ?? 0);
        });

        const unitsSoldByMonth = (monthsLowerCase || []).reduce((acc, key, i) => {
            acc[key] = soldUnitsByMonth?.[i] ?? 0;
            return acc;
        }, {});

        const annualUnitsSold = soldUnitsByMonth.reduce(
            (acc, val) => acc + (val || 0),
            0
        );

        const varianceUnitsByMonth = (monthsLowerCase || []).reduce((acc, monthKey) => {
            const actualUnits = Number(unitsSoldByMonth?.[monthKey] ?? 0);
            const forecastUnits = Number(monthlyUnitsByMonth?.[monthKey] ?? 0);
            acc[monthKey] = actualUnits - forecastUnits;
            return acc;
        }, {});

        const annualVarianceByUnits = Object.values(varianceUnitsByMonth).reduce(
            (sum, val) => sum + (val || 0),
            0
        );

        // Compute YTD now that monthly maps are initialized
        const yearNum = Number(selectedForecastYear);
        const currentYear = new Date().getFullYear();
        const isCurrentYear = yearNum === currentYear;
        const limit = isCurrentYear ? (new Date().getMonth() + 1) : 12;
        const end = Math.min((monthsLowerCase || []).length, limit);
        let ytdForecastRev = 0;
        let ytdActualRev = 0;
        let ytdForecastUnits = 0;
        let ytdActualUnits = 0;
        for (let i = 0; i < end; i++) {
            const k = monthsLowerCase[i];
            ytdForecastRev += Number(monthlyForecastByMonth?.[k] || 0);
            ytdActualRev += Number(monthlyRevenueByMonth?.[k] || 0);
            ytdForecastUnits += Number(monthlyUnitsByMonth?.[k] || 0);
            ytdActualUnits += Number(unitsSoldByMonth?.[k] || 0);
        }
        const ytdVarianceRev = Number(ytdActualRev - ytdForecastRev);
        const ytdVariancePct = ytdForecastRev > 0 ? (ytdVarianceRev / ytdForecastRev) * 100 : 0;

        return {
            annual_actual: annualActual,
            annual_forecast: annualForecast,
            annual_variance: totalDifference,
            annual_units_variance: annualVarianceByUnits,
            annual_units_sold: annualUnitsSold,
            annual_units: annualUnits,
            monthly_units_variance_by_month: varianceUnitsByMonth,
            monthly_units_sold_by_month: unitsSoldByMonth,
            monthly_variance_by_month: monthlyDifferenceByMonth,
            monthly_actuals_by_month: monthlyRevenueByMonth,
            monthly_forecast_by_month: monthlyForecastByMonth,
            monthly_forecast_units_by_month: monthlyUnitsByMonth,
            // Persist YTD metrics per product
            ytd_forecast_revenue: ytdForecastRev,
            ytd_actual_revenue: ytdActualRev,
            ytd_variance_revenue: ytdVarianceRev,
            ytd_forecast_units: ytdForecastUnits,
            ytd_actual_units: ytdActualUnits,
            ytd_variance_percent: ytdVariancePct,
        }
    }

    const updateForecastProduct = (productId, changes) => {
        setForecastProducts((prev) => (prev || []).map((p) => {
            if (p.id === productId) {
                const merged = { ...p, ...changes };
                const derived = getDerivedData(merged);
                return { ...merged, ...derived };
            }
            return p;
        }));
    };

    useEffect(() => {
        // Recompute derived values when allocation or annual goal changes
        setForecastProducts((prev) => (prev || []).map((p) => ({
            ...p,
            ...getDerivedData(p)
        })));
    }, [monthlyAllocationData, annualGoal, summaryActuals, plannedActuals]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const getTopEls = () => Array.from(container.querySelectorAll('[data-sticky-top="true"]'));
        const getLeftEls = () => Array.from(container.querySelectorAll('[data-sticky-left="true"]'));
        const getAllEls = () => Array.from(container.querySelectorAll('[data-sticky-top="true"], [data-sticky-left="true"]'));

        const applyCombined = () => {
            const x = container.scrollLeft;
            const rect = container.getBoundingClientRect();

            // compute vertical offset under external sticky header(s)
            const topEls = getTopEls();
            let dy = 0;
            if (topEls.length > 0) {
                const headerHeight = topEls[0].offsetHeight || 0;
                const maxDy = Math.max(0, rect.height - headerHeight);
                const attrOffset = Number(container.getAttribute('data-sticky-top-offset') || 0);
                const cssVar = parseInt(getComputedStyle(container).getPropertyValue('--forecast-sticky-offset') || '0', 10) || 0;
                const anchor = document.querySelector('[data-forecast-sticky-anchor]');
                const anchorOffset = anchor ? Math.round(anchor.getBoundingClientRect().height || 0) : 0;
                const stickyOffset = Math.max(0, attrOffset, cssVar, anchorOffset);
                dy = Math.max(0, Math.min(maxDy, stickyOffset - rect.top));
            }

            getAllEls().forEach((el) => {
                const hasTop = el.getAttribute('data-sticky-top') === 'true';
                const hasLeft = el.getAttribute('data-sticky-left') === 'true';
                const tx = hasLeft ? x : 0;
                const ty = hasTop ? dy : 0;
                el.style.position = 'relative';
                el.style.transform = `translate(${tx}px, ${ty}px)`;
                el.style.willChange = 'transform';
                el.style.zIndex = hasTop && hasLeft ? '40' : hasTop ? '35' : '30';
                if (!el.style.backgroundColor) el.style.backgroundColor = '#f5f5f5';
            });
        };

        const onScroll = () => applyCombined();
        const onWindowScroll = () => applyCombined();

        container.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('scroll', onWindowScroll, { passive: true });
        // initial
        applyCombined();

        return () => {
            container.removeEventListener('scroll', onScroll);
            window.removeEventListener('scroll', onWindowScroll);
            // clean transforms to avoid persisting across unmounts
            getAllEls().forEach((el) => { el.style.transform = ''; });
        };
    }, [forecastProducts, selectedForecastYear]);

    // Build global totals (across all rendered products) for header totals rows
    const headerTotals = useMemo(() => {
        const monthKeys = monthsLowerCase || [];
        const base = Object.fromEntries(monthKeys.map((m) => [m, 0]));
        const totals = {
            annualActual: 0,
            annualForecast: 0,
            monthlyActuals: { ...base },
            monthlyForecasts: { ...base },
        };
        const list = (renderedForecastProducts && renderedForecastProducts.length > 0)
            ? renderedForecastProducts
            : (forecastProducts || []);
        (list || []).forEach((p) => {
            totals.annualActual += Number(p?.annual_actual || 0);
            totals.annualForecast += Number(p?.annual_forecast || 0);
            monthKeys.forEach((m) => {
                totals.monthlyActuals[m] += Number(p?.monthly_actuals_by_month?.[m] || 0);
                totals.monthlyForecasts[m] += Number(p?.monthly_forecast_by_month?.[m] || 0);
            });
        });
        return totals;
    }, [renderedForecastProducts, forecastProducts]);

    const buildTotalsFromDerived = (product) => {
        const monthKeys = monthsLowerCase || [];
        const monthActuals = Object.fromEntries(monthKeys.map((m) => [m, Number(product?.monthly_actuals_by_month?.[m] || 0)]));
        const monthForecasts = Object.fromEntries(monthKeys.map((m) => [m, Number(product?.monthly_forecast_by_month?.[m] || 0)]));
        const totalActual = Object.values(monthActuals).reduce((s, v) => s + Number(v || 0), 0);
        const totalForecast = Object.values(monthForecasts).reduce((s, v) => s + Number(v || 0), 0);
        return { totalActual, totalForecast, monthActuals, monthForecasts };
    };

    return (
        <div className="px-6">
            <div>
                <input 
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search products..."
                    className="border-input flex min-w-0 rounded-md border bg-white px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-[200px] h-9"
                />
            </div>
            <div className="overflow-x-auto" ref={scrollContainerRef} data-sticky-top-offset="64">
                <div className="w-full">
                    <table className="w-full table-auto border-separate border-spacing-x-0 border-spacing-y-0">
                        <thead>
                            <tr>
                                <th className="text-left font-medium text-gray-700 py-2 px-1 whitespace-nowrap align-bottom" data-sticky-left="true" data-sticky-top="true">Product</th>
                                <th className="text-left font-medium text-gray-700 py-2 px-1 align-bottom" data-sticky-top="true">AOV</th>
                                <th className="text-left font-medium text-gray-700 py-2 px-1 whitespace-nowrap align-bottom" data-sticky-top="true">
                                    <div className="w-20 relative flex items-start justify-between flex-col gap-2">
                                        <span className={`inline-flex items-center justify-center rounded-md border px-1 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 border-transparent ${revPctBadgeClass}`}>
                                            Total: {revPctTotalRounded}%
                                        </span>
                                        <span>Rev %</span>
                        </div>
                                </th>
                                <th className="text-left font-medium text-gray-700 py-2 px-1 whitespace-nowrap align-bottom pl-3" data-sticky-top="true">Annual Total</th>
                                {monthsLong.map((month) => (
                                    <th key={month} className="text-left font-medium text-gray-700 py-2 px-1 w-[150px] whitespace-nowrap align-bottom" data-sticky-top="true">
                                        {month}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Global totals row (Actuals) under Annual Total and Months */}
                            <tr>
                                <td className="px-1 py-1 text-gray-700 font-medium whitespace-nowrap" data-sticky-left="true">Actual Total</td>
                                <td className="px-1 py-1"></td>
                                <td className="px-1 py-1"></td>
                                <td className="font-semibold px-1 py-1 pl-3">${formatPrice(Number(headerTotals.annualActual || 0))}</td>
                                {monthsLowerCase.map((m) => (
                                    <td key={`totals-actual-${m}`} className="font-semibold px-1 py-1">${formatPrice(Number(headerTotals.monthlyActuals?.[m] || 0))}</td>
                                ))}
                            </tr>
                            {/* Global totals row (Forecast) */}
                            <tr>
                                <td className="px-1 py-0 text-gray-700 whitespace-nowrap" data-sticky-left="true">Forecast Total</td>
                                <td className="px-1 py-0"></td>
                                <td className="px-1 py-0"></td>
                                <td className="text-gray-500 px-1 py-0 pl-3">${formatPrice(Number(headerTotals.annualForecast || 0))}</td>
                                {monthsLowerCase.map((m) => (
                                    <td key={`totals-forecast-${m}`} className="text-gray-500 px-1 py-0">${formatPrice(Number(headerTotals.monthlyForecasts?.[m] || 0))}</td>
                                ))}
                            </tr>
                            {(renderedForecastProducts || []).map((forecastProduct) => {
                                const ytd = {
                                    forecastRev: Number(forecastProduct?.ytd_forecast_revenue || 0),
                                    actualRev: Number(forecastProduct?.ytd_actual_revenue || 0),
                                    varianceRev: Number(forecastProduct?.ytd_variance_revenue || 0),
                                    forecastUnits: Number(forecastProduct?.ytd_forecast_units || 0),
                                    actualUnits: Number(forecastProduct?.ytd_actual_units || 0),
                                    variancePct: Number(forecastProduct?.ytd_variance_percent || 0),
                                };
                            return (
                                    <>
                                        <tr key={`${forecastProduct.id}-header`} className="[&>td]:pt-3">
                                            <td className="py-0 px-1" data-sticky-left="true">
                                                <div className="flex items-center gap-3 justify-between">
                                                    <div className="text-base font-bold">
                                                {(groupsBySelect || []).find((group) => group.value === forecastProduct.id)?.label || ""}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setForecastProducts((prev) => (prev || []).filter((p) => p.id !== forecastProduct.id));
                                                }}
                                                className="p-1 rounded hover:bg-gray-100"
                                                aria-label="Remove product from forecast"
                                            >
                                                        <XMarkIcon className="h-5 w-5 text-black cursor-pointer" />
                                            </button>
                                        </div>
                                            </td>
                                            <td className="py-0 px-1">
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">
                                                    $
                                                </span>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    className="w-24 text-right bg-white rounded-md pl-6 pr-3 py-1 appearance-none"
                                                    placeholder="0.00"
                                                    value={aovInputById[forecastProduct.id] ?? String(Number(forecastProduct.aov ?? 0))}
                                                    onChange={(e) => {
                                                        let value = String(e.target.value ?? "");
                                                        value = value.replace(/[^0-9.]/g, "");
                                                        const firstDot = value.indexOf(".");
                                                        if (firstDot !== -1) {
                                                            const before = value.slice(0, firstDot + 1);
                                                            const after = value.slice(firstDot + 1).replace(/\./g, "");
                                                            value = before + after;
                                                        }
                                                        if (value.includes(".")) {
                                                            const [i, d = ""] = value.split(".");
                                                            value = `${i}.${d.slice(0, 2)}`;
                                                        }
                                                        const parsed = parseFloat(value);
                                                        if (!isNaN(parsed)) {
                                                            updateForecastProduct(forecastProduct.id, { aov: parsed });
                                                        }
                                                        setAovInputById((prev) => ({ ...prev, [forecastProduct.id]: value }));
                                                    }}
                                                    onBlur={() => {
                                                        const raw = aovInputById[forecastProduct.id];
                                                        if (raw == null) {
                                                            return;
                                                        }
                                                        const parsed = parseFloat(raw);
                                                        if (!isNaN(parsed)) {
                                                            const normalized = Number(parsed.toFixed(2));
                                                            updateForecastProduct(forecastProduct.id, { aov: normalized });
                                                        }
                                                        setAovInputById((prev) => {
                                                            const next = { ...prev };
                                                            delete next[forecastProduct.id];
                                                            return next;
                                                            });
                                                        }}
                                                />
                                            </div>
                                            </td>
                                            <td className="py-0 px-1">
                                            <input 
                                                type="text"
                                                inputMode="decimal"
                                                placeholder="0.00"
                                                value={pctRevInputById[forecastProduct.id] ?? String(Number(forecastProduct.pctRev ?? 0))}
                                                onChange={(e) => {
                                                    let value = String(e.target.value ?? "");
                                                    value = value.replace(/[^0-9.]/g, "");
                                                    const firstDot = value.indexOf(".");
                                                    if (firstDot !== -1) {
                                                        const before = value.slice(0, firstDot + 1);
                                                        const after = value.slice(firstDot + 1).replace(/\./g, "");
                                                        value = before + after;
                                                    }
                                                    if (value.includes(".")) {
                                                        const [i, d = ""] = value.split(".");
                                                        value = `${i}.${d.slice(0, 2)}`;
                                                    }
                                                    let [intPart = "", decPart] = value.split(".");
                                                    intPart = intPart.replace(/^0+(?=\d)/, "");
                                                    intPart = intPart.slice(0, 3);
                                                    value = decPart !== undefined ? `${intPart || "0"}.${decPart}` : intPart;
                                                    const parsed = parseFloat(value);
                                                    if (!isNaN(parsed)) {
                                                        const clamped = Math.max(0, Math.min(100, parsed));
                                                        if (clamped !== parsed) {
                                                            value = String(clamped);
                                                        }
                                                        updateForecastProduct(
                                                            forecastProduct.id,
                                                            { pctRev: Number(clamped.toFixed(2)) }
                                                        );
                                                    }
                                                    setPctRevInputById((prev) => ({ ...prev, [forecastProduct.id]: value }));
                                                }}
                                                onBlur={() => {
                                                    const raw = pctRevInputById[forecastProduct.id];
                                                        if (raw == null) {
                                                            return;
                                                        }
                                                    const parsed = parseFloat(raw);
                                                        if (!isNaN(parsed)) {
                                                            const clampedNum = Math.max(0, Math.min(100, parsed));
                                                    const normalized = Number(clampedNum.toFixed(2));
                                                    updateForecastProduct(forecastProduct.id, { pctRev: normalized });
                                                        }
                                                    setPctRevInputById((prev) => {
                                                        const next = { ...prev };
                                                        delete next[forecastProduct.id];
                                                        return next;
                                                    });
                                                }}
                                                className="w-20 text-right bg-white rounded-md pl-6 pr-3 py-1 appearance-none"
                                            />
                                            </td>
                                            <td className="py-0 px-1"></td>
                                            {monthsLowerCase.map((m) => (
                                                <td key={`${forecastProduct.id}-blank-${m}`} className="py-1 px-1"></td>
                                            ))}
                                        </tr>
                                        
                                        <tr key={`${forecastProduct.id}-actions`}>
                                            <td className="py-0 px-1" colSpan={4 + monthsLowerCase.length}>
                                                <div className="w-fit" data-sticky-left="true">
                                        <button
                                            onClick={() => {
                                                setOpenedActionsByRow((prev) => {
                                                    const isSame = prev.id === forecastProduct.id && prev.opened;
                                                    return { id: forecastProduct.id, opened: !isSame };
                                                });
                                            }}
                                            ref={(el) => { actionsBtnRefs.current[forecastProduct.id] = el; }}
                                            className="inline-flex items-center rounded bg-black text-white px-5 py-1 text-xs font-medium hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/40"
                                        >
                                            {forecastProduct.start_month ? `Start: ${monthKeysForecastEnum[forecastProduct.start_month] || ""}` : "Set period"}
                                        </button>
                                        <DropdownByProductSettings 
                                            isOpen={openedActionsByRow?.opened && openedActionsByRow?.id === forecastProduct.id}
                                            forecastProduct={forecastProduct}
                                            setForecastProducts={setForecastProducts}
                                            cancelClick={() => {
                                                            setOpenedActionsByRow({ id: "", opened: false })
                                            }}
                                            onChangeSettings={(changes) => updateForecastProduct(forecastProduct.id, changes)}
                                            anchorEl={actionsBtnRefs.current[forecastProduct.id]}
                                            usePortal
                                        />
                                    </div>
                                            </td>
                                        </tr>
                                        {/* Actuals row */}
                                        <tr key={`${forecastProduct.id}-row-actuals`} className={`${!((activeActuals || []).includes(ACTUAL)) ? hiddenRowClassname : ""} [&>td]:align-top [&>td]:pt-2`}>
                                            <td className="text-gray-700 px-1 py-0 align-top" data-sticky-left="true">Actuals</td>
                                            <td className="font-semibold px-1 py-0 bg-green-50 rounded-t-xl border-t border-x border-green-500 relative align-top leading-none" colSpan={2}>
                                                <div className="absolute -top-7 text-base">YTD</div>
                                                ${formatPrice(Number(ytd.actualRev || 0))}
                                            </td>
                                            <td className="font-semibold px-1 py-0 pl-3">${formatPrice(Number(forecastProduct?.annual_actual || 0))}</td>
                                            {monthsLowerCase.map((month) => (
                                                <td key={`${forecastProduct.id}-actuals-${month}`} className="font-semibold px-1 py-0">${formatPrice(Number(forecastProduct?.monthly_actuals_by_month?.[month] || 0))}</td>
                                            ))}
                                        </tr>
                                        {/* Forecast row */}
                                        <tr key={`${forecastProduct.id}-row-forecast`} className={`${!((activeActuals || []).includes(FORECAST)) ? hiddenRowClassname : ""} [&>td]:align-top`}>
                                            <td className="text-gray-700 px-1 py-0" data-sticky-left="true">
                                                <div>Forecast</div>
                                                <div className="invisible text-xs mt-0.5">—</div>
                                            </td>
                                            <td className="text-gray-500 px-1 py-0 bg-green-50 border-x border-green-500" colSpan={2}>${formatPrice(Number(ytd.forecastRev || 0))}</td>
                                            <td className="text-gray-500 px-1 py-0 pl-3">${formatPrice(Number(forecastProduct?.annual_forecast || 0))}</td>
                                            {monthsLowerCase.map((month) => (
                                                <td key={`${forecastProduct.id}-forecast-${month}`} className="text-gray-500 px-1 py-0">${formatPrice(Number(forecastProduct?.monthly_forecast_by_month?.[month] || 0))}</td>
                                            ))}
                                        </tr>
                                        {/* Variance revenue row */}
                                        <tr key={`${forecastProduct.id}-row-variance`} className={`${!((activeActuals || []).includes(VARIANCE)) ? hiddenRowClassname : ""} [&>td]:align-top`}>
                                            <td className="text-gray-700 px-1 py-0" data-sticky-left="true">Variance</td>
                                            <td className={`font-medium px-1 py-0 ${getVarianceColor(Number(ytd.varianceRev || 0))} bg-green-50 border-x border-green-500`} colSpan={2}>{Number(ytd.varianceRev || 0) >= 0 ? '+' : '-'}${formatPrice(Math.abs(Number(ytd.varianceRev || 0)).toFixed(2))}</td>
                                            <td className={`font-medium px-1 py-0 pl-3 ${getVarianceColor(Number(forecastProduct?.annual_variance || 0))}`}>${formatPrice(Number(forecastProduct?.annual_variance || 0))}</td>
                                            {monthsLowerCase.map((month) => (
                                                <td key={`${forecastProduct.id}-variance-${month}`} className={`font-medium px-1 py-0 ${getVarianceColor(Number(forecastProduct?.monthly_variance_by_month?.[month] || 0))}`}>${formatPrice(Number(forecastProduct?.monthly_variance_by_month?.[month] || 0))}</td>
                                            ))}
                                        </tr>
                                        {/* Units forecast row */}
                                        <tr key={`${forecastProduct.id}-row-units-forecast`} className={`${!((activeActuals || []).includes(UNITS_FORECAST)) ? hiddenRowClassname : ""} [&>td]:align-top`}>
                                            <td className="text-gray-700 px-1 py-0" data-sticky-left="true">
                                                <div>Units</div>
                                                <div className="invisible text-xs mt-0.5">—</div>
                                            </td>
                                            <td className="text-gray-500 px-1 py-0 bg-green-50 border-x border-green-500" colSpan={2}>{Number(ytd.forecastUnits || 0).toLocaleString('en-US')}</td>
                                            <td className="text-gray-500 px-1 py-0 pl-3">{forecastProduct?.annual_units != null ? Number(forecastProduct?.annual_units).toLocaleString('en-US') : '—'}</td>
                                            {monthsLowerCase.map((month) => (
                                                <td key={`${forecastProduct.id}-units-forecast-${month}`} className="text-gray-500 px-1 py-0">{forecastProduct?.monthly_forecast_units_by_month?.[month] != null ? Number(forecastProduct.monthly_forecast_units_by_month[month]).toLocaleString('en-US') : '—'}</td>
                                            ))}
                                        </tr>
                                        {/* Units sold actual row */}
                                        <tr key={`${forecastProduct.id}-row-units-sold`} className={`${!((activeActuals || []).includes(UNITS_SOLD_ACTUAL)) ? hiddenRowClassname : ""} [&>td]:align-top`}>
                                            <td className="text-gray-700 px-1 py-0" data-sticky-left="true">Units Sold</td>
                                            <td className="text-gray-900 px-1 py-0 bg-green-50 border-x border-green-500" colSpan={2}>{Number(ytd.actualUnits || 0).toLocaleString('en-US')}</td>
                                            <td className="text-gray-900 px-1 py-0 pl-3">{forecastProduct?.annual_units_sold != null ? Number(forecastProduct?.annual_units_sold).toLocaleString('en-US') : '—'}</td>
                                            {monthsLowerCase.map((month) => (
                                                <td key={`${forecastProduct.id}-units-sold-${month}`} className="text-gray-900 px-1 py-0">{forecastProduct?.monthly_units_sold_by_month?.[month] != null ? Number(forecastProduct.monthly_units_sold_by_month[month]).toLocaleString('en-US') : '—'}</td>
                                            ))}
                                        </tr>
                                        {/* Units variance row */}
                                        <tr key={`${forecastProduct.id}-row-units-variance`} className={`${!((activeActuals || []).includes(UNITS_ACTIVE)) ? hiddenRowClassname : ""} [&>td]:align-top [&>td]:pb-2`}>
                                            <td className="text-gray-700 px-1 py-0" data-sticky-left="true">Variance</td>
                                            <td className={`${getVarianceColor(Number(ytd.variancePct || 0))} px-1 py-0 bg-green-50 rounded-b-xl border-b border-x border-green-500`} colSpan={2}>{Number(ytd.variancePct || 0) > 0 ? '+' : (Number(ytd.variancePct || 0) < 0 ? '-' : '')}{Math.abs(Number(ytd.variancePct || 0)).toFixed(2)}%</td>
                                            <td className={`${getVarianceColor(Number(forecastProduct?.annual_units_variance || 0))} px-1 py-0 pl-3`}>{forecastProduct?.annual_units_variance != null ? Number(forecastProduct?.annual_units_variance).toLocaleString('en-US') : '—'}</td>
                                            {monthsLowerCase.map((month) => (
                                                <td key={`${forecastProduct.id}-units-variance-${month}`} className={`${getVarianceColor(Number(forecastProduct?.monthly_units_variance_by_month?.[month] || 0))} px-1 py-0`}>{forecastProduct?.monthly_units_variance_by_month?.[month] != null ? Number(forecastProduct.monthly_units_variance_by_month[month]).toLocaleString('en-US') : '—'}</td>
                                            ))}
                                        </tr>
                                    </>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="py-2 mb-44">
                        {!addProductToForecastOpened && (
                            <button
                                onClick={() => {
                                    setAddProductToForecastOpened(true);
                                }}
                                className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] rounded-md gap-1.5 px-3 h-8 text-black hover:text-black hover:bg-gray-100"
                            >
                                <PlusIcon className="h-5 w-5" />
                                Add Product
                            </button>
                        )}
                        {addProductToForecastOpened && (
                            <>
                                <div className="min-w-[132px] max-w-[360px] py-2 flex items-center gap-2 overflow-visible">
                                    <OTBProductSelect
                                        options={availableProductOptions}
                                        selectedProduct={selectedProduct}
                                        handleChange={setSelectedProduct}
                                        optionsIsLoading={false}
                                        placeholder="Select product"
                                        containerClassName="!w-full"
                                        buttonClassName="!bg-black !text-white"
                                        dropdownClassName="!shadow-xl"
                                        optionActiveClassName="!bg-black !text-white"
                                    />
                                </div>
                                <div className="col-start-3 py-2 flex items-center gap-2">
                                    <button
                                        onClick={() => {
                                            if (selectedProduct) {
                                                setForecastProducts((prev) => {
                                                    const newItemBase = { 
                                                        id: selectedProduct, 
                                                        aov: 0,
                                                        pctRev: 0, 
                                                        ramp: "none", 
                                                        percent_mode: "prorate", 
                                                        start_month: "jan",
                                                        end_month: "dec" 
                                                    };
                                                    const newItem = { ...newItemBase, ...getDerivedData(newItemBase) };
                                                    return [...prev, newItem]
                                                });
                                                setSelectedProduct(null);
                                                setAddProductToForecastOpened(false);
                                            }
                                        }}
                                        className="whitespace-nowrap text-sm font-medium transition-all outline-none shadow-xs gap-1.5 h-8 inline-flex items-center justify-center rounded-md bg-black text-white px-4 py-2 hover:bg-black"
                                    >
                                        Add
                                    </button>
                                    <button
                                        onClick={() => {
                                            setAddProductToForecastOpened(false);
                                            setSelectedProduct(null);
                                        }}
                                        className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all rounded-md gap-1.5 px-3 h-8"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}